import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, FileText, Scale, AlertTriangle } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import SEO from "@/components/common/SEO";
import { getPageSeo } from "@/lib/seo";

const Section = ({ icon: Icon, title, children, testId }) => (
  <motion.section
    {...fadeUp}
    data-testid={testId}
    className="glass-card p-7 md:p-9"
  >
    <div className="flex items-start gap-4">
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30 flex-shrink-0">
        <Icon className="h-5 w-5 text-[color:var(--sp-gold)]" />
      </span>
      <div className="flex-1">
        <h2 className="font-display text-2xl md:text-3xl text-white leading-tight">
          {title}
        </h2>
        <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-white/75">
          {children}
        </div>
      </div>
    </div>
  </motion.section>
);

const TermsPage = () => {
  const lastUpdated = "January 2026";
  const seo = getPageSeo("terms");
  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        robots={seo.robots}
      />
      <section
        data-testid="terms-hero"
        className="relative pt-40 pb-12"
      >
        <div className="absolute inset-0 hero-overlay opacity-80" />
        <div className="container-narrow relative">
          <Link
            to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/65 hover:text-[color:var(--sp-gold)] transition-colors mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back Home
          </Link>
          <motion.p
            {...fadeUp}
            className="text-[11px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]"
          >
            <span className="gold-divider" /> Legal · Terms
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl"
          >
            Terms & <span className="italic text-white/85">Conditions</span>.
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 max-w-2xl text-white/70 text-base leading-relaxed"
          >
            Please read these Terms & Conditions carefully before using the
            Seetai Properties website or engaging with our services.
          </motion.p>
          <p className="mt-4 text-xs tracking-[0.24em] uppercase text-white/45">
            Last updated · {lastUpdated}
          </p>
        </div>
      </section>

      <section data-testid="terms-content" className="pb-24">
        <div className="container-narrow space-y-6">
          <Section icon={FileText} title="1. Acceptance of Terms" testId="terms-section-acceptance">
            <p>
              By accessing or using the website operated by{" "}
              <strong>Seetai Properties Private Limited</strong> (hereinafter
              “Seetai Properties”, “we”, “our”, or “us”), you confirm that you
              have read, understood, and agree to be bound by these Terms &
              Conditions and our Privacy Policy.
            </p>
            <p>
              If you do not agree with any part of these Terms, please
              discontinue use of the website. Your continued access constitutes
              ongoing acceptance.
            </p>
          </Section>

          <Section icon={Scale} title="2. Use of Website & Content" testId="terms-section-use">
            <p>
              All content displayed on this website — including project
              renderings, layout plans, photography, brand marks, voiceovers,
              and text — is the intellectual property of Seetai Properties or
              its licensors. You may view and download content solely for
              personal, non-commercial reference.
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-white/70">
              <li>
                You may not reproduce, redistribute, or commercially exploit any
                part of the website without our prior written consent.
              </li>
              <li>
                Master layouts, brochures, and CGIs are <em>indicative only</em>{" "}
                and may differ from the final delivered product. Final
                specifications shall be governed by the registered Agreement to
                Sell.
              </li>
              <li>
                You will not attempt to compromise the security of the site, run
                automated scrapers, or misuse forms / endpoints in any manner.
              </li>
            </ul>
          </Section>

          <Section icon={AlertTriangle} title="3. Project Information & RERA" testId="terms-section-rera">
            <p>
              Silva City and other projects featured on this website are
              showcased for awareness and pre-launch interest registration. All
              statutory approvals, permissions, and RERA registrations apply as
              and when issued. The information presented (areas, pricing,
              timelines, amenities) is{" "}
              <strong>subject to change at the sole discretion</strong> of
              Seetai Properties and shall be finalised only in the registered
              sale documents.
            </p>
            <p>
              Any commitment of investment must be made after personal
              verification of project documents, RERA registration certificates,
              title clearance reports, and statutory approvals at our corporate
              office.
            </p>
          </Section>

          <Section icon={Scale} title="4. Pricing, Bookings & Payments" testId="terms-section-payments">
            <p>
              Indicative pricing displayed on the website (where applicable) is
              not a binding offer. All bookings are confirmed only upon receipt
              of the booking amount via formally accepted payment channels and
              issuance of an allotment letter signed by an authorised
              representative of Seetai Properties.
            </p>
            <p>
              All payments must be made strictly through bank channels (cheque,
              NEFT, RTGS, IMPS) in the name of{" "}
              <strong>Seetai Properties Private Limited</strong>. Cash payments
              or third-party transfers are not accepted and Seetai Properties
              will not be responsible for any consequences arising from such
              transactions.
            </p>
          </Section>

          <Section icon={AlertTriangle} title="5. Limitation of Liability" testId="terms-section-liability">
            <p>
              To the maximum extent permitted by law, Seetai Properties shall
              not be liable for any direct, indirect, incidental, consequential,
              or punitive damages arising out of (a) your access to or use of
              the website, (b) reliance on any content displayed, or (c) any
              technical interruption beyond our reasonable control.
            </p>
            <p>
              The website is provided on an “as-is” and “as-available” basis
              without warranties of any kind, whether express or implied.
            </p>
          </Section>

          <Section icon={FileText} title="6. Third-Party Links & Services" testId="terms-section-thirdparty">
            <p>
              The website may contain links to third-party properties, social
              channels, or messaging services (such as WhatsApp). Seetai
              Properties does not control and is not responsible for the content
              or practices of these third parties. Your engagement with such
              services is governed by their respective terms.
            </p>
          </Section>

          <Section icon={Scale} title="7. Governing Law & Jurisdiction" testId="terms-section-law">
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of India. Any disputes arising shall be subject to the
              exclusive jurisdiction of the competent courts at{" "}
              <strong>Pune, Maharashtra</strong>.
            </p>
          </Section>

          <Section icon={FileText} title="8. Changes to These Terms" testId="terms-section-changes">
            <p>
              We may revise these Terms from time to time. The “Last updated”
              date at the top of this page reflects the most recent revision.
              Material changes will be highlighted via a banner on the website
              for a reasonable period.
            </p>
          </Section>

          <Section icon={FileText} title="9. Contact Us" testId="terms-section-contact">
            <p>
              Questions regarding these Terms can be sent to{" "}
              <a
                href="mailto:info@seetaiproperties.com"
                className="text-[color:var(--sp-gold)] hover:underline"
              >
                info@seetaiproperties.com
              </a>{" "}
              or via the{" "}
              <Link
                to="/contact"
                className="text-[color:var(--sp-gold)] hover:underline"
              >
                Contact page
              </Link>
              .
            </p>
          </Section>
        </div>
      </section>
    </>
  );
};

export default TermsPage;
